import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Stack from '@mui/material/Stack';
import { UploadButton, UploadButtonHandle } from '../buttons/UploadButton';
import { DeleteButton } from '../DeleteButton';
import { TextField } from '../TextField';

export interface FileInputProps {
  disabled?: boolean;
  id?: string;
  isSampleMap?: boolean;
  value: File | null;
  onChange?: (value: File | null) => void;
}

export const FileInput = ({
  disabled,
  id = `fileField-${uuidv4()}`,
  isSampleMap = false,
  value,
  onChange,
}: FileInputProps) => {
  const uploadButtonRef = useRef<UploadButtonHandle>(null);

  const handleUploadButtonChange = useCallback(
    (file: File) => onChange?.(file),
    [onChange]
  );

  const handleStartUpload = useCallback(() => {
    uploadButtonRef.current?.reset();
  }, []);

  const handleDeleteButtonClick = useCallback(() => {
    onChange?.(null);
    uploadButtonRef.current?.reset();
  }, [onChange]);

  useEffect(() => {
    if (!value) uploadButtonRef.current?.reset();
  }, [value]);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap={1}
      width="100%"
    >
      <TextField
        disabled={true}
        id={id}
        placeholder={`Upload ${isSampleMap ? '.csv' : 'file...'}`}
        value={value?.name || ''}
      />

      <Stack direction="row">
        <DeleteButton
          disabled={!value || disabled}
          trackLabelVariant="wflow.parameter.fileDelete"
          onClick={handleDeleteButtonClick}
        />
        <UploadButton
          accept={isSampleMap ? '.csv' : undefined}
          disabled={disabled}
          ref={uploadButtonRef}
          trackLabel="wflow.parameter.fileUpload"
          onChange={handleUploadButtonChange}
          onClick={handleStartUpload}
        />
      </Stack>
    </Stack>
  );
};

FileInput.displayName = 'FileInput';
